import React, { useState } from "react";
import { format } from "date-fns";

const ViewMessageModal = ({ show, handleClose, message }) => {
  const [isRead, setIsRead] = useState(message?.status === 'read');

  if (!show || !message) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const handleReply = () => {
    window.open(`mailto:${message.email}?subject=Re: Your Message #${message.id}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`message-modal-backdrop ${show ? "show" : ""}`} 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`message-modal-container ${show ? "show" : ""}`}>
        <div className="message-modal">
          {/* Header */}
          <div className="message-modal-header">
            <div className="header-content">
              <div className="sender-avatar">
                <span className="avatar-text">
                  {getInitials(message.firstName, message.lastName)}
                </span>
              </div>
              <div className="sender-info">
                <h1 className="sender-name">
                  {message.firstName} {message.lastName}
                </h1>
                <div className="sender-details">
                  <div className="email-container">
                    <i className="ri-mail-fill email-icon"></i>
                    <span className="email-address" title={message.email}>
                      {message.email}
                    </span>
                  </div>
                  <div className="time-container">
                    <i className="ri-time-fill time-icon"></i>
                    <span className="message-time">
                      {formatDate(message.created_at || message.date)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button 
              className="modal-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* Separator */}
          <div className="separator"></div>

          {/* Content */}
          <div className="message-modal-content">
            {/* Message Subject */}
            <div className="message-subject-container">
              <h2 className="message-subject">
                <i className="ri-message-2-fill"></i>
                Contact Form Message
              </h2>
              <div className="message-status-container">
                <span className="message-id">
                  <i className="ri-hashtag"></i>ID: {message.id}
                </span>
              </div>
            </div>

            {/* Message Content */}
            <div className="message-content-container">
              <div className="message-text-content">
                <div className="message-text-wrapper">
                  {message.message.split('\n').map((line, index) => (
                    <p key={index} className="message-line">
                      {line || <br />}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="contact-details-container">
              <div className="contact-detail-row">
                <div className="contact-label">
                  <i className="ri-user-fill"></i>
                  <span>FULL NAME</span>
                </div>
                <div className="contact-value-wrapper">
                  <span className="contact-value-text">
                    {message.firstName} {message.lastName}
                  </span>
                  <button 
                    className="copy-icon-btn"
                    onClick={() => copyToClipboard(`${message.firstName} ${message.lastName}`)}
                    title="Copy name"
                  >
                    <i className="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>

              <div className="contact-detail-row">
                <div className="contact-label">
                  <i className="ri-phone-fill"></i>
                  <span>PHONE NUMBER</span>
                </div>
                <div className="contact-value-wrapper">
                  <a 
                    href={`tel:${message.phone}`}
                    className="contact-value-link"
                  >
                    {message.phone}
                  </a>
                  <button 
                    className="copy-icon-btn"
                    onClick={() => copyToClipboard(message.phone)}
                    title="Copy phone number"
                  >
                    <i className="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>

              <div className="contact-detail-row">
                <div className="contact-label">
                  <i className="ri-mail-fill"></i>
                  <span>EMAIL ADDRESS</span>
                </div>
                <div className="contact-value-wrapper">
                  <a 
                    href={`mailto:${message.email}`}
                    className="contact-value-email"
                    title={message.email}
                  >
                    {message.email}
                  </a>
                  <button 
                    className="copy-icon-btn"
                    onClick={() => copyToClipboard(message.email)}
                    title="Copy email address"
                  >
                    <i className="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Separator */}
          <div className="footer-separator"></div>

          {/* Footer Actions */}
          <div className="message-modal-footer">
            <div className="action-buttons-container">
              <button 
                className="action-btn btn-close"
                onClick={handleClose}
              >
                <i className="ri-close-line"></i>
                CLOSE
              </button>
              
              <button 
                className="action-btn btn-reply"
                onClick={handleReply}
              >
                <i className="ri-reply-line"></i>
                REPLY VIA EMAIL
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Backdrop */
        .message-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(3px);
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
        }
        
        .message-modal-backdrop.show {
          opacity: 1;
          visibility: visible;
        }

        /* Modal Container */
        .message-modal-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1001;
          padding: 20px;
        }
        
        .message-modal-container.show {
          opacity: 1;
          visibility: visible;
        }

        /* Modal */
        .message-modal {
          width: 100%;
          max-width: 650px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          transform: scale(0.95) translateY(10px);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .message-modal-container.show .message-modal {
          transform: scale(1) translateY(0);
        }

        /* Header */
        .message-modal-header {
          padding: 24px 32px 20px;
          background: linear-gradient(135deg, #267c47ff 0%, #21cea2ff 100%);
          position: relative;
        }

        .header-content {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .sender-avatar {
          width: 56px;
          height: 56px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(255, 255, 255, 0.3);
          flex-shrink: 0;
        }

        .avatar-text {
          font-size: 20px;
          font-weight: bold;
          color: white;
          letter-spacing: 0.5px;
        }

        .sender-info {
          flex: 1;
          min-width: 0;
        }

        .sender-name {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 700;
          color: white;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sender-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .email-container,
        .time-container {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
        }

        .email-icon,
        .time-icon {
          font-size: 14px;
          opacity: 0.8;
          flex-shrink: 0;
        }

        .email-address,
        .message-time {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }

        .modal-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 18px;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: rotate(90deg);
        }

        /* Separator */
        .separator {
          height: 1px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.3) 20%, 
            rgba(255, 255, 255, 0.3) 80%, 
            transparent 100%
          );
          margin: 0 32px;
        }

        /* Content */
        .message-modal-content {
          padding: 24px 32px;
        }

        /* Message Subject */
        .message-subject-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .message-subject {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .message-subject i {
          color: #4f46e5;
          font-size: 20px;
        }

        .message-status-container {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .message-id {
          font-size: 12px;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 4px;
          font-weight: 500;
        }

        /* Message Content */
        .message-content-container {
          margin-bottom: 24px;
        }

        .message-text-content {
          background: #f9fafb;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          border-left: 4px solid #4f46e5;
        }

        .message-text-wrapper {
          line-height: 1.6;
        }

        .message-line {
          margin: 0 0 12px 0;
          color: #374151;
          font-size: 14px;
        }

        .message-line:last-child {
          margin-bottom: 0;
        }

        /* Contact Details - FIXED SECTION */
        .contact-details-container {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-top: 24px;
        }

        .contact-detail-row {
          display: flex;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #e5e7eb;
          min-height: 48px;
        }

        .contact-detail-row:last-child {
          border-bottom: none;
        }

        .contact-label {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 700;
          width: 160px;
          flex-shrink: 0;
        }

        .contact-label i {
          font-size: 16px;
          color: #4f46e5;
          width: 20px;
          text-align: center;
        }

        .contact-value-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex: 1;
          min-width: 0;
          gap: 16px;
        }

        .contact-value-text,
        .contact-value-link,
        .contact-value-email {
          color: #111827;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }

        .contact-value-link,
        .contact-value-email {
          color: #1d4ed8;
        }

        .contact-value-link:hover,
        .contact-value-email:hover {
          color: #4338ca;
          text-decoration: underline;
        }

        .copy-icon-btn {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          transition: all 0.2s ease;
          flex-shrink: 0;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }

        .copy-icon-btn:hover {
          background: #f3f4f6;
          color: #4f46e5;
        }

        /* Footer Separator */
        .footer-separator {
          height: 1px;
          background: #e5e7eb;
          margin: 0 32px;
        }

        /* Footer - FIXED BUTTON ALIGNMENT */
        .message-modal-footer {
          padding: 20px 32px;
          display: flex;
          justify-content: flex-end;
        }

        .action-buttons-container {
          display: flex;
          gap: 12px;
          width: 100%;
          justify-content: space-between;
        }

        .action-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          font-size: 12px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          min-height: 44px;
          flex: 1;
          max-width: 48%;
        }

        .btn-close {
          background:linear-gradient(135deg, #b91010ff 0%, #ff4b4bff 100%);
          color: #ffffffff;
          border: 2px solid #e5e7eb;
        }

        .btn-close:hover {
          background: #e70707ff;
          color: #634b4bff;
          transform: translateY(-1px);
        }

        .btn-reply {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .btn-reply:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        /* Scrollbar Styling */
        .message-modal-content {
          max-height: 300px;
          overflow-y: auto;
        }

        .message-modal-content::-webkit-scrollbar {
          width: 6px;
        }

        .message-modal-content::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .message-modal-content::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }

        .message-modal-content::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Responsive Design */
        @media (max-width: 640px) {
          .message-modal {
            max-width: 95%;
          }
          
          .message-modal-header,
          .message-modal-content,
          .message-modal-footer {
            padding: 20px;
          }
          
          .header-content {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }
          
          .sender-info {
            text-align: center;
          }
          
          .message-subject-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .contact-detail-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            padding: 12px 0;
          }
          
          .contact-label {
            width: 100%;
            justify-content: flex-start;
          }
          
          .contact-value-wrapper {
            width: 100%;
          }
          
          .action-buttons-container {
            flex-direction: column;
            gap: 10px;
          }
          
          .action-btn {
            max-width: 100%;
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .sender-name {
            font-size: 20px;
          }
          
          .email-address,
          .message-time {
            font-size: 12px;
          }
          
          .contact-label {
            font-size: 11px;
          }
          
          .contact-value-text,
          .contact-value-link,
          .contact-value-email {
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

export default ViewMessageModal;